import { CheckIcon, EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import { type ReactNode, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { PASSWORD_MIN_LENGTH, type PasswordRule } from '@/utils/passwordPolicy';

import {
  fieldClass,
  fieldInvalid,
  focusRing,
  labelClass,
  tone,
  useAuthT,
} from './kit';
import { PASSWORD_HINT, RULE_LABELS } from './passwordCopy';

/**
 * A password input with a show/hide button. With `rules` (sign-up, new
 * password) it shows one line of guidance until the visitor starts typing,
 * then the policy as a checklist that ticks off as they type.
 */
export default function PasswordField({
  id,
  name = 'password',
  label,
  value,
  onChange,
  autoComplete,
  placeholder,
  rules,
  hintClassName,
  invalid,
  disabled,
  required = true,
  minLength,
  labelAside,
  describedBy,
}: {
  id: string;
  name?: string;
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  placeholder?: string;
  rules?: PasswordRule[];
  /**
   * Set to always render the one-line guidance (styled by this class) even
   * without `rules`: /login shows or hides it by mode before hydration.
   */
  hintClassName?: string;
  invalid?: boolean;
  disabled?: boolean;
  required?: boolean;
  minLength?: number;
  /** Beside the label, e.g. "Forgot password?". */
  labelAside?: ReactNode;
  /** Ids of other text that describes the field (a mismatch message). */
  describedBy?: string;
}) {
  const { t } = useAuthT();
  const [visible, setVisible] = useState(false);
  const rulesId = `${id}-requirements`;
  const describedByIds =
    [rules ? rulesId : null, describedBy].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div>
      <div className='flex items-baseline justify-between gap-3'>
        <label htmlFor={id} className={labelClass}>
          {label}
        </label>
        {labelAside}
      </div>
      <div className='relative mt-2'>
        <input
          id={id}
          name={name}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize='none'
          autoCorrect='off'
          spellCheck={false}
          required={required}
          minLength={minLength}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={invalid || undefined}
          aria-describedby={describedByIds}
          className={clsxm(fieldClass, 'pe-12', invalid && fieldInvalid)}
        />
        <button
          type='button'
          onClick={() => setVisible((current) => !current)}
          disabled={disabled}
          aria-label={visible ? t('Hide password') : t('Show password')}
          aria-pressed={visible}
          aria-controls={id}
          className={clsxm(
            'absolute inset-y-1 end-1 flex w-10 items-center justify-center rounded-lg transition-colors hover:bg-[#0f0f28]/[0.05] hover:text-[#10101c] disabled:pointer-events-none dark:hover:bg-white/10 dark:hover:text-white',
            tone.quiet,
            focusRing,
          )}
        >
          {visible ? (
            <EyeOffIcon className='h-5 w-5' aria-hidden='true' />
          ) : (
            <EyeIcon className='h-5 w-5' aria-hidden='true' />
          )}
        </button>
      </div>
      {(rules || hintClassName !== undefined) &&
        (value.length === 0 || !rules ? (
          <p
            id={rulesId}
            className={clsxm(
              'mt-2 text-[13px] leading-5',
              tone.quiet,
              hintClassName,
            )}
          >
            {t(PASSWORD_HINT, { min: PASSWORD_MIN_LENGTH })}
          </p>
        ) : (
          <ul
            id={rulesId}
            aria-label={t('Password requirements')}
            className='mt-2.5 grid grid-cols-1 gap-x-4 gap-y-1.5 min-[400px]:grid-cols-2'
          >
            {rules.map((rule) => (
              <li
                key={rule.key}
                className={clsxm(
                  'flex items-start gap-2 text-[13px] leading-5 transition-colors',
                  rule.passed
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : tone.quiet,
                )}
              >
                <span
                  aria-hidden='true'
                  className={clsxm(
                    'mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full transition-colors',
                    rule.passed
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-300'
                      : 'ring-1 ring-inset ring-[#0f0f28]/20 dark:ring-white/20',
                  )}
                >
                  {rule.passed && <CheckIcon className='h-3 w-3' />}
                </span>
                <span>
                  {t(RULE_LABELS[rule.key], { min: PASSWORD_MIN_LENGTH })}
                  <span className='sr-only'>
                    {rule.passed ? ` (${t('done')})` : ''}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}
